import { Box, type BoxProps } from '@chakra-ui/react'
import type { ReactNode } from 'react'

/**
 * A two-tone incised "groove rule": a 1px dark line above a 1px light line, so
 * the cut appears to catch the light from below. This is the only boundary the
 * brand uses between sections (docs/brand/brand-design.md §surface-language).
 */
export const GrooveRule = (props: BoxProps) => (
  <Box aria-hidden borderTop="1px solid" borderTopColor="line.dark" {...props}>
    <Box borderTop="1px solid" borderTopColor="line.light" />
  </Box>
)

type StrataTone = 'canvas' | 'band'

interface StrataProps {
  /** Which stratum tone this band carries. Alternate down the page. */
  tone?: StrataTone
  /** Draw the groove rule along the band's top edge. Omit on the first band. */
  topRule?: boolean
  /** Max width of the centred content column inside the full-width band. */
  contentMaxW?: BoxProps['maxW']
  /** Passed through to the section element (id for anchor links, etc.). */
  id?: string
  /** Marks the band as a footer landmark instead of a generic section. */
  as?: BoxProps['as']
  children: ReactNode
  /** Extra props for the inner content column (e.g. textAlign). */
  contentProps?: BoxProps
}

/**
 * A full-width horizontal "stratum" — the carved-stone band that replaces
 * boxed cards as the page's structural unit. Sections alternate between the
 * `canvas` and `band` stone tones and are separated only by groove rules; no
 * content is ever outlined or floated on a shadow.
 */
export const Strata = ({
  tone = 'canvas',
  topRule = true,
  contentMaxW = '2xl',
  id,
  as = 'section',
  children,
  contentProps,
}: StrataProps) => (
  <Box as={as} id={id} bg={tone === 'band' ? 'bg.band' : 'bg.canvas'} position="relative">
    {topRule && <GrooveRule />}
    <Box maxW={contentMaxW} mx="auto" px={4} py={{ base: 12, md: 16 }} {...contentProps}>
      {children}
    </Box>
  </Box>
)

export default Strata
